import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SectionHeader from "@/components/SectionHeader";
import PostComposer from "@/components/PostComposer";
import PostCard from "@/components/PostCard";
import UserCard from "@/components/UserCard";
import StoriesBar from "@/components/StoriesBar";
import DiscordWidget from "@/components/DiscordWidget";
import { fetchFeed, fetchTrendingTags, fetchSuggestedUsers } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { sfx } from "@/lib/sounds";
import { Flame, TrendingUp, Users, Bookmark, Hash, Sparkles } from "lucide-react";

const TABS = [
  { key: "recent", label: "Recent", icon: TrendingUp },
  { key: "trending", label: "Trending", icon: Flame },
  { key: "following", label: "Following", icon: Users, auth: true },
  { key: "bookmarked", label: "Saved", icon: Bookmark, auth: true },
  { key: "mine", label: "My Posts", icon: Sparkles, auth: true },
];

export default function Community() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || "";
  const [tab, setTab] = useState("recent");
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trending, setTrending] = useState([]);
  const [suggested, setSuggested] = useState([]);

  const load = async (reset = false) => {
    setLoading(true);
    try {
      const q = { tab, limit: 20 };
      if (tag) q.tag = tag;
      if (!reset && cursor) q.cursor = cursor;
      const data = await fetchFeed(q);
      setPosts((prev) => reset ? data.posts : [...prev, ...data.posts]);
      setCursor(data.next_cursor);
    } catch (err) {
      console.debug("feed load failed", err?.message);
    }
    setLoading(false);
  };

  useEffect(() => { setPosts([]); setCursor(null); load(true); /* eslint-disable-next-line */ }, [tab, tag]);
  useEffect(() => {
    fetchTrendingTags().then(setTrending).catch((err) => console.debug("trending tags failed", err?.message));
    fetchSuggestedUsers(5).then(setSuggested).catch((err) => console.debug("suggested users failed", err?.message));
  }, []);

  const clearTag = () => setParams({});

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-community">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Pluse"
          title="What's happening on LostDC"
          subtitle="Stories, builds, clips and polls — shared with the whole LostDRKC network."
        />

        <div className="mt-4"><StoriesBar /></div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6 mt-4">
          {/* MAIN column */}
          <div className="space-y-4">
            <PostComposer onPosted={(p) => setPosts((x) => [p, ...x])} />

            {tag && (
              <div className="rounded-xl glass p-3 flex items-center gap-2" data-testid="active-tag">
                <Hash size={13} className="text-[#FFBF00]" />
                <span className="text-sm">Filtering by <span className="text-[#FFBF00]">#{tag}</span></span>
                <button onClick={clearTag} className="ml-auto text-[10px] uppercase tracking-widest text-white/60 hover:text-white">Clear</button>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
              {TABS.filter((t) => !t.auth || user).map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => { setTab(key); sfx.click(); }}
                        data-testid={`feed-tab-${key}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap ${
                          tab === key ? "border-[#FFBF00] text-[#FFBF00]" : "border-transparent text-white/60 hover:text-white"
                        }`}>
                  <Icon size={12} /> {label}
                </button>
              ))}
            </div>

            <div className="space-y-4">
              {posts.map((p) => (
                <PostCard key={p.id} post={p}
                          onDelete={(id) => setPosts((x) => x.filter((q) => q.id !== id))}
                          onUpdate={(np) => setPosts((x) => x.map((q) => q.id === np.id ? np : q))} />
              ))}
              {posts.length === 0 && !loading && (
                <div className="rounded-2xl glass p-12 text-center text-white/50">
                  <div className="font-hero text-2xl mb-2">The feed is quiet</div>
                  <div className="text-sm">Be the first to post something!</div>
                </div>
              )}
              {cursor && (
                <div className="text-center">
                  <button onClick={() => load(false)} disabled={loading}
                          className="px-6 py-2 rounded-full border border-white/15 hover:border-[#FFBF00]/50 text-xs uppercase tracking-widest disabled:opacity-50">
                    {loading ? "Loading…" : "Load more"}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-4 lg:sticky lg:top-24 self-start">
            <DiscordWidget />
            <div className="rounded-2xl glass p-5">
              <div className="flex items-center gap-2 mb-3">
                <Flame size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Trending Tags</div>
              </div>
              {trending.length === 0 ? (
                <div className="text-sm text-white/40">No trending tags yet.</div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {trending.map((t) => (
                    <Link key={t.tag} to={`/community?tag=${encodeURIComponent(t.tag)}`}
                          className="text-[11px] px-2 py-1 rounded-full border border-white/10 hover:border-[#FFBF00]/50">
                      <span className="text-[#FFBF00]">#{t.tag}</span> <span className="text-white/40 font-mono">{t.count}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl glass p-5">
              <div className="flex items-center gap-2 mb-3">
                <Users size={14} className="text-[#FFBF00]" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Suggested</div>
              </div>
              <div className="space-y-2">
                {suggested.map((u) => <UserCard key={u.id} user={u} compact />)}
                {suggested.length === 0 && <div className="text-sm text-white/40">Nothing here yet.</div>}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
